import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';

import { LogCategoriesComponent } from '../../logbook/log-categories/log-categories.component';
import { GetAllLogCategories } from '../../logbook/logbook.actions';
import { LogbookState } from '../../logbook/logbook.state';
import { GeneralSettings } from '../../models/GeneralSettings';
import { HolidayDay } from '../../models/HolidayDay';
import LogCategory from '../../models/Logbook/LogCategory';
import { FetchGeneralSettings, UpdateFinancialsIncludeCurrentMonth, UpdatePostmarkApiToken } from '../settings.actions';
import { SettingsState } from '../settings.state';
import { UpdateDaysPerMonthModalComponent } from '../update-days-per-month-modal/update-days-per-month-modal.component';
import { UpdateHolidayDaysModalComponent } from '../update-holiday-days-modal/update-holiday-days-modal.component';

@Component({
    selector: 'app-general-settings',
    templateUrl: './general-settings.component.html',
    styleUrls: ['./general-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class GeneralSettingsComponent implements OnInit, OnDestroy {
    @Select(SettingsState.GeneralSettings) generalSettings$: Observable<GeneralSettings>;
    generalSettings: GeneralSettings;

    @Select(LogbookState.LogCategories) logCategories$: Observable<LogCategory[]>;
    logCategories: LogCategory[];

    postmarkApiToken = '';
    showPostmarkToken = false;

    private subscriptions = new Subscription();

    constructor(private modalService: NgbModal, private logger: NGXLogger, private store: Store, private toastr: ToastrService) {}

    ngOnInit(): void {
        this.fetchData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    openUpdateDaysPerMonthModal() {
        const modalRef = this.modalService.open(UpdateDaysPerMonthModalComponent, { windowClass: 'modal-prompt', animation: false });
        modalRef.componentInstance.days = this.generalSettings.daysPerMonth;
    }

    openUpdateHolidayDaysModal() {
        const modalRef = this.modalService.open(UpdateHolidayDaysModalComponent, { windowClass: 'modal-pane', animation: false });
        modalRef.componentInstance.holidayDays = this.generalSettings.holidayDays.map((holidayDay) => {
            const holiday: HolidayDay = {
                description: holidayDay.description,
                localDate: new Date(holidayDay.localDate),
            };
            return holiday;
        });
    }

    openLogbookCategoriesModal() {
        const modalRef = this.modalService.open(LogCategoriesComponent, { windowClass: 'modal-pane', animation: false });
        modalRef.componentInstance.logCategories = this.logCategories;
    }

    savePostmarkApiToken() {
        if (!this.postmarkApiToken || this.postmarkApiToken.trim() === '') {
            this.toastr.warning('Please enter a Postmark API token.');
            return;
        }
        this.store.dispatch(new UpdatePostmarkApiToken(this.postmarkApiToken.trim())).subscribe({
            next: () => this.toastr.success('Postmark API token saved.'),
            error: () => this.toastr.error('Failed to save Postmark API token.'),
        });
    }

    togglePostmarkTokenVisibility() {
        this.showPostmarkToken = !this.showPostmarkToken;
    }

    toggleFinancialsIncludeCurrentMonth(include: boolean) {
        this.store.dispatch(new UpdateFinancialsIncludeCurrentMonth(include)).subscribe({
            next: () =>
                this.toastr.success(include ? 'Financial overviews now include the current month.' : 'Financial overviews now stop at the previous month.'),
            error: () => this.toastr.error('Failed to update financial month setting.'),
        });
    }

    private fetchData() {
        this.subscriptions.add(
            this.generalSettings$.subscribe((settings) => {
                if (!settings) {
                    this.logger.debug('Fetching General settings');
                    this.store.dispatch(new FetchGeneralSettings());
                    return;
                }
                this.generalSettings = settings;
                this.postmarkApiToken = settings.postmarkApiToken || '';
            }),
        );

        this.subscriptions.add(
            this.logCategories$.subscribe((categories) => {
                if (categories === undefined) {
                    this.store.dispatch(new GetAllLogCategories());
                    return;
                }
                this.logCategories = [...categories];
            }),
        );
    }
}
